import React, { useState, useRef } from 'react';
import ChatbotFixed from './ChatbotFixed';

const AutomatedTester = () => {
  const [context, setContext] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [queryCount, setQueryCount] = useState(0);
  const [showChatbot, setShowChatbot] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [currentQuery, setCurrentQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const intervalRef = useRef(null);

  const addDebugLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev.slice(-10), { timestamp, message, type }]);
  };

  // Generate contextual query using LLM
  const generateContextualQuery = async () => {
    addDebugLog('🧠 Calling LLM to generate query...', 'info');
    
    try {
      let contextualFramework;
      
      if (conversationHistory.length === 0) {
        // First question - use context only
        contextualFramework = `${context}. Generate the first customer question about flight booking.`;
      } else {
        // Follow-up questions - include conversation history
        const recentHistory = conversationHistory.slice(-2); // Last 2 exchanges
        const historyText = recentHistory.map(h => `Customer: ${h.question} | Chatbot: ${h.response}`).join(' || ');
        contextualFramework = `${context}. Previous conversation: ${historyText}`;
      }
      
      addDebugLog(`📡 Sending to LLM: ${contextualFramework.substring(0, 100)}...`, 'info');
      
      const response = await fetch('https://jzdt-dev-func-ai-test.azurewebsites.net/api/generate-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          framework: contextualFramework
        })
      });

      addDebugLog(`📡 LLM API response: ${response.status}`, response.ok ? 'success' : 'warning');

      if (response.ok) {
        const data = await response.json();
        const generatedQuery = data.query?.trim();
        if (generatedQuery && generatedQuery.length > 5) {
          addDebugLog(`✅ LLM generated: "${generatedQuery}"`, 'success');
          return generatedQuery;
        }
      }
      
      throw new Error(`LLM API failed with status ${response.status}`);
      
    } catch (error) {
      addDebugLog(`⚠️ LLM failed: ${error.message}, using fallback`, 'warning');
      
      // Fallback progression if LLM fails
      const fallbackQueries = [
        'I need to book a flight. What are your available routes?',
        'I want to fly from Kuwait to Dubai on March 15th.',
        'What are the available departure times?',
        'Can you show me the flight prices?',
        'What is the baggage allowance?'
      ];
      
      const queryIndex = queryCount % fallbackQueries.length;
      const fallbackQuery = fallbackQueries[queryIndex];
      addDebugLog(`🔄 Using fallback: "${fallbackQuery}"`, 'info');
      return fallbackQuery;
    }
  };

  // Simplified response capture - just track questions for now
  const captureResponse = (question) => {
    // Add question to history immediately with current count
    setConversationHistory(prev => {
      const newEntry = { 
        question, 
        response: 'Response pending...', 
        queryNumber: queryCount + 1 
      };
      return [...prev, newEntry];
    });
    
    addDebugLog(`📝 Added Q${queryCount + 1} to history`, 'success');
  };

  // Wait for element with retry logic
  const waitForElement = (selectors, maxRetries = 10, delay = 1000) => {
    return new Promise((resolve) => {
      let retries = 0;
      
      const checkForElement = () => {
        for (const selector of selectors) {
          const elements = document.querySelectorAll(selector);
          for (const element of elements) {
            if (element && element.offsetParent !== null && !element.disabled) {
              resolve(element);
              return;
            }
          }
        }
        
        retries++;
        if (retries < maxRetries) {
          addDebugLog(`⏳ Waiting for element... (${retries}/${maxRetries})`, 'info');
          setTimeout(checkForElement, delay);
        } else {
          resolve(null);
        }
      };
      
      checkForElement();
    });
  };

  // Simulate typing and sending with better waiting
  const simulateQuery = async (query) => {
    addDebugLog(`⌨️ Starting to type: "${query.substring(0, 30)}..."`, 'info');
    setCurrentQuery(query);
    setIsTyping(true);
    
    // Wait for input field to be available
    const inputSelectors = [
      'input[placeholder*="Enter next query"]',
      'input[placeholder*="أدخل استفسارك التالي"]',
      'input[type="text"]:not([disabled]):not([style*="display: none"])',
      '.chatbot-fixed-container input[type="text"]',
      'input[type="text"]',
      'input'
    ];
    
    addDebugLog('🔍 Looking for input field...', 'info');
    const input = await waitForElement(inputSelectors, 15, 1000);
    
    if (!input) {
      addDebugLog('❌ Input field not found after waiting', 'warning');
      setIsTyping(false);
      return;
    }

    addDebugLog('✅ Found input field!', 'success');
    
    // Type the query with React-compatible events
    input.focus();
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    nativeInputValueSetter.call(input, query);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    
    // Wait a bit then send
    setTimeout(async () => {
      setIsTyping(false);
      addDebugLog('✅ Typing completed, looking for send button...', 'success');
      
      // Wait for send button
      const buttonSelectors = [
        'button[title*="Send"]',
        'button[style*="borderRadius: 50%"]',
        'button:has(svg)',
        'button[type="submit"]',
        'button:not([disabled])'
      ];
      
      const sendButton = await waitForElement(buttonSelectors, 5, 500);
      
      if (sendButton) {
        addDebugLog('✅ Clicking send button', 'success');
        sendButton.click();
        captureResponse(query);
      } else {
        addDebugLog('⚠️ Send button not found, trying Enter key', 'warning');
        input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
        captureResponse(query);
      }
    }, 1500);
  };

  // Run test cycle
  const runTestCycle = async () => {
    if (!showChatbot) {
      addDebugLog('⚠️ Chatbot not shown, skipping', 'warning');
      return;
    }
    
    // Get current count and increment immediately
    const currentCount = queryCount;
    addDebugLog(`🔄 Starting test cycle with count: ${currentCount}`, 'info');
    
    try {
      const query = await generateContextualQuery();
      addDebugLog(`📝 Will send query #${currentCount + 1}: "${query}"`, 'success');
      
      // Increment count BEFORE sending to ensure progression
      setQueryCount(prev => {
        const newCount = prev + 1;
        addDebugLog(`➕ Incremented count to: ${newCount}`, 'success');
        return newCount;
      });
      
      await simulateQuery(query);
      
      addDebugLog(`✅ Test cycle completed`, 'success');
    } catch (error) {
      addDebugLog(`❌ Test cycle failed: ${error.message}`, 'warning');
    }
  };

  // Start testing
  const startTesting = () => {
    if (!context.trim()) {
      alert('Please provide context for the LLM before starting testing.');
      return;
    }
    
    // Reset everything first
    setQueryCount(0);
    setConversationHistory([]);
    setDebugLogs([]);
    setCurrentQuery('');
    
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    setIsRunning(true);
    setShowChatbot(true);
    
    addDebugLog('🚀 Starting fresh testing session...', 'info');
    addDebugLog('🔄 Reset query count to 0', 'success');
    
    setTimeout(() => {
      runTestCycle();
      intervalRef.current = setInterval(runTestCycle, 25000);
    }, 5000);
  };

  // Stop testing
  const stopTesting = () => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Clear and restart
  const clearAndRestart = () => {
    // Stop any running tests
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Reset all state
    setQueryCount(0);
    setConversationHistory([]);
    setDebugLogs([]);
    setCurrentQuery('');
    setShowChatbot(false);
    
    addDebugLog('🔄 Complete reset performed', 'success');
    
    setTimeout(() => setShowChatbot(true), 500);
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#f5f7fa',
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#e53e3e',
          padding: '20px 30px',
          color: 'white'
        }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>🧪 Contextual Chatbot Tester</h1>
          <p style={{ margin: '5px 0 0', opacity: 0.8 }}>AI-powered contextual testing with conversation flow</p>
        </div>

        {/* Main Content - Two Sections */}
        <div style={{ display: 'flex', height: '700px' }}>
          {/* Left Section - Context Input */}
          <div style={{ 
            width: '40%', 
            padding: '20px', 
            borderRight: '2px solid #eee',
            backgroundColor: '#f8f9fa'
          }}>
            <h3 style={{ margin: '0 0 15px', color: '#e53e3e' }}>📝 Context & Controls</h3>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Context for LLM:
              </label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Enter context for the LLM to generate relevant questions...

Example: You are testing a flight booking system. Generate questions about flight schedules, prices, and booking process."
                style={{
                  width: '100%',
                  height: '200px',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                disabled={isRunning}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
              {!isRunning ? (
                <button
                  onClick={startTesting}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  ▶️ Start Testing
                </button>
              ) : (
                <button
                  onClick={stopTesting}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  ⏹️ Stop Testing
                </button>
              )}
              
              <button
                onClick={clearAndRestart}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                disabled={isRunning}
              >
                🔄 Reset
              </button>
              
              <button
                onClick={() => {
                  if (showChatbot) {
                    addDebugLog('🧪 Manual test triggered', 'info');
                    runTestCycle();
                  }
                }}
                style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                disabled={isRunning || !showChatbot}
              >
                🧪 Test Once
              </button>
              
              <button
                onClick={() => {
                  const nextQuery = queryCount + 1;
                  addDebugLog(`🔢 Next will be query #${nextQuery}`, 'info');
                }}
                style={{
                  backgroundColor: '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                🔢 Check Next
              </button>
            </div>
            
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
              Queries sent: <strong>{queryCount}</strong>
              {isRunning && <span style={{ color: '#28a745', marginLeft: '10px' }}>● Running</span>}
              {isTyping && <span style={{ color: '#007bff', marginLeft: '10px' }}>⌨️ Typing</span>}
            </div>
            
            {/* Conversation History */}
            <div>
              <h4 style={{ margin: '0 0 10px', color: '#666' }}>💬 Conversation History</h4>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '10px',
                fontSize: '12px'
              }}>
                {conversationHistory.length === 0 ? (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>Conversation history will appear here...</div>
                ) : (
                  conversationHistory.map((item, index) => (
                    <div key={index} style={{ marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                      <div style={{ color: '#007bff', fontWeight: 'bold' }}>Q{index + 1}: {item.question}</div>
                      <div style={{ color: '#28a745', marginTop: '2px' }}>A: {item.response}</div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Manual Response Capture Button */}
              <button
                onClick={() => {
                  const chatbotContainer = document.querySelector('.chatbot-fixed-container, [class*="chatbot"], [class*="chat"]');
                  if (chatbotContainer) {
                    const allTexts = Array.from(chatbotContainer.querySelectorAll('*'))
                      .map(el => el.textContent?.trim())
                      .filter(text => text && text.length > 10)
                      .slice(-10);
                    
                    addDebugLog(`🔍 Found texts: ${allTexts.length}`, 'info');
                    console.log('All chatbot texts:', allTexts);
                    alert(`Found ${allTexts.length} text elements. Check console for details.`);
                  }
                }}
                style={{
                  backgroundColor: '#ffc107',
                  color: '#212529',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '5px 10px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  marginTop: '5px'
                }}
              >
                🔍 Debug Response Capture
              </button>
            </div>
          </div>
          
          {/* Right Section - Chatbot */}
          <div style={{ width: '60%', padding: '20px', position: 'relative' }}>
            <h3 style={{ margin: '0 0 15px', color: '#e53e3e' }}>🤖 Chatbot Testing</h3>
            
            {/* Debug Panel */}
            {showChatbot && (
              <div style={{ 
                marginBottom: '15px',
                backgroundColor: '#fff3cd',
                borderRadius: '4px',
                padding: '10px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h5 style={{ margin: 0, color: '#856404' }}>🔍 Debug Console</h5>
                  <button
                    onClick={() => setDebugLogs([])}
                    style={{
                      backgroundColor: '#ffc107',
                      color: '#212529',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      fontSize: '11px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear
                  </button>
                </div>
                
                {currentQuery && (
                  <div style={{ marginBottom: '8px', fontSize: '12px' }}>
                    <strong>Current:</strong> "{currentQuery.substring(0, 40)}..."
                    {isTyping && <span style={{ color: '#007bff', marginLeft: '8px' }}>⌨️</span>}
                  </div>
                )}
                
                <div style={{ 
                  maxHeight: '60px', 
                  overflowY: 'auto',
                  fontSize: '11px',
                  backgroundColor: '#fff',
                  padding: '5px',
                  borderRadius: '3px'
                }}>
                  {debugLogs.slice(-3).map((log, index) => (
                    <div key={index} style={{ 
                      color: log.type === 'success' ? '#28a745' : log.type === 'warning' ? '#ffc107' : '#6c757d'
                    }}>
                      {log.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!showChatbot ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                color: '#666',
                backgroundColor: '#fff',
                borderRadius: '8px',
                height: 'calc(100% - 120px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <h4 style={{ margin: '0 0 10px', color: '#e53e3e' }}>🧪 Ready for Testing</h4>
                <p style={{ margin: '0 0 15px', fontSize: '14px' }}>Add context and start testing</p>
                <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>LLM will generate contextual questions</p>
              </div>
            ) : (
              <div style={{
                height: 'calc(100% - 120px)',
                border: '2px solid #e53e3e',
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <ChatbotFixed />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomatedTester;